// A state object that can be listened to. About sixty lines, no dependencies.
//
// The client's state was a plain object mutated in place from ~60 call sites,
// with no way to observe it — so every mutator had to know, and hand-call, each
// thing downstream of it. Changing the active variable meant remembering to call
// nine separate refreshers; adding a tenth meant finding all of them. That is the
// coupling that makes a 4,000-line module inevitable: the only way a new piece of
// UI can react to a change is for the mutator to import it.
//
// `set` closes that. A mutator states WHAT CHANGED; the things that care say so
// once, at wiring time, and are called for it.
//
// Deliberately small:
//   - Shallow, by identity. `set({ recipe })` notifies; `state.recipe.w.x = 1`
//     does not. Anything with listeners is replaced, not edited in place.
//   - Synchronous, in registration order. The old code's call order was load
//     bearing in places (a legend rebuilt before the chips it measures), so
//     subscribers run in the order they were added — same as before, just
//     declared instead of transcribed.
//   - Each listener runs at most once per `set`, however many of its keys moved.
//   - Reads stay direct: `store.state.dateIdx`. There is no getter ceremony, and
//     existing `state.x` reads keep working unchanged.

export interface Store<T extends object> {
  // The live object. Read freely; write through `set` when anything listens.
  readonly state: T;
  // Apply a patch and notify. Returns the keys that actually changed, which is
  // also what makes the notification testable.
  set(patch: Partial<T>): (keyof T)[];
  // Call `fn` after any of `keys` changes. Returns an unsubscribe function.
  subscribe(keys: (keyof T)[], fn: () => void): () => void;
}

interface Listener<T> {
  keys: Set<keyof T>;
  fn: () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  const state = initial;
  const listeners: Listener<T>[] = [];

  return {
    state,

    set(patch: Partial<T>): (keyof T)[] {
      const changed: (keyof T)[] = [];
      for (const k of Object.keys(patch) as (keyof T)[]) {
        const next = patch[k] as T[keyof T];
        if (Object.is(state[k], next)) continue;
        state[k] = next;
        changed.push(k);
      }
      if (!changed.length) return changed;
      // Snapshot: a listener may subscribe or unsubscribe while reacting, and
      // that must not disturb the round already in flight.
      for (const l of listeners.slice()) {
        if (changed.some((k) => l.keys.has(k))) l.fn();
      }
      return changed;
    },

    subscribe(keys: (keyof T)[], fn: () => void): () => void {
      const listener: Listener<T> = { keys: new Set(keys), fn };
      listeners.push(listener);
      return () => {
        const i = listeners.indexOf(listener);
        if (i >= 0) listeners.splice(i, 1);
      };
    },
  };
}
